import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { pedidosAPI, despachoNacionalesAPI, preparativosAPI } from '../../services/api';

export const fetchNacionalOrders = createAsyncThunk(
  'despachoNacionales/fetchNacionalOrders',
  async ({ temporadaId, semana }, { rejectWithValue }) => {
    try {
      console.log('[fetchNacionalOrders] Calling API with:', { temporadaId, semana });
      const response = await pedidosAPI.fetchNacionalOrdersByWeek(temporadaId, semana);
      console.log('[fetchNacionalOrders] API response:', response);
      return response.data;
    } catch (error) {
      console.error('[fetchNacionalOrders] Error:', error);
      return rejectWithValue(error.message || 'Error al obtener pedidos nacionales');
    }
  }
);

export const updateOrderTicket = createAsyncThunk(
  'despachoNacionales/updateOrderTicket',
  async ({ orderId, ticketDespacho }, { rejectWithValue }) => {
    try {
      const response = await pedidosAPI.updateOrderTicket(orderId, ticketDespacho);
      return { orderId, ticketDespacho, data: response.data };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar ticket');
    }
  }
);

export const fetchInventarioStatus = createAsyncThunk(
  'despachoNacionales/fetchInventarioStatus',
  async ({ temporadaId, semana }, { rejectWithValue }) => {
    try {
      const response = await pedidosAPI.checkInventarioStatus(temporadaId, semana);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al verificar estado de inventario');
    }
  }
);

export const rebajarInventario = createAsyncThunk(
  'despachoNacionales/rebajarInventario',
  async ({ temporadaId, semana }, { rejectWithValue }) => {
    try {
      const response = await pedidosAPI.rebajarInventarioNacional(temporadaId, semana);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al rebajar inventario');
    }
  }
);

export const previewFacturaLocalNacional = createAsyncThunk(
  'despachoNacionales/previewFacturaLocalNacional',
  async ({ pedidoId, fecha }, { rejectWithValue }) => {
    try {
      const invoiceData = await despachoNacionalesAPI.previewFacturaLocalNacional({ pedidoId, fecha });
      return invoiceData;
    } catch (error) {
      return rejectWithValue(error.message || 'Error obteniendo vista previa de factura');
    }
  }
);

export const generarFacturaLocalNacional = createAsyncThunk(
  'despachoNacionales/generarFacturaLocalNacional',
  async ({ pedidoId, fecha }, { rejectWithValue }) => {
    try {
      const result = await despachoNacionalesAPI.generarFacturaLocalNacional({ pedidoId, fecha });
      return { pedidoId, ...result };
    } catch (error) {
      return rejectWithValue(error.message || 'Error iniciando generación de factura');
    }
  }
);

export const fetchSyncEvent = createAsyncThunk(
  'despachoNacionales/fetchSyncEvent',
  async ({ syncEventId }, { rejectWithValue }) => {
    try {
      const result = await preparativosAPI.getSyncEvent({ syncEventId });
      return result.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error obteniendo estado de sincronización');
    }
  }
);

const initialState = {
  orders: [],
  loading: false,
  error: null,
  inventarioRebajado: false,
  inventarioData: null,
  updatingTicket: {},
  rebajandoInventario: false,
  // Factura Local Nacional
  previewData: null,
  previewLoading: false,
  previewError: null,
  generatingFactura: {},
  generationError: {},
  syncEventStatus: {},
  pollingSync: {}
};

const despachoNacionalesSlice = createSlice({
  name: 'despachoNacionales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNacionalOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNacionalOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        
        // Initialize syncEventStatus from existing pedido_factura_local data
        console.log('[fetchNacionalOrders.fulfilled] Processing orders:', action.payload.length);
        action.payload.forEach(order => {
          console.log('[fetchNacionalOrders.fulfilled] Order:', order.id, 'pedido_factura_local:', order.pedido_factura_local);
          
          // Handle both array and single object from Supabase
          let facturaLocal = null;
          if (order.pedido_factura_local) {
            if (Array.isArray(order.pedido_factura_local) && order.pedido_factura_local.length > 0) {
              facturaLocal = order.pedido_factura_local[0];
            } else if (!Array.isArray(order.pedido_factura_local)) {
              facturaLocal = order.pedido_factura_local;
            }
          }
          
          if (facturaLocal) {
            console.log('[fetchNacionalOrders.fulfilled] facturaLocal data:', facturaLocal);
            if (facturaLocal.folio && facturaLocal.sync_event_id) {
              console.log('[fetchNacionalOrders.fulfilled] Initializing syncEventStatus for order:', order.id);
              state.syncEventStatus[order.id] = {
                syncEventId: facturaLocal.sync_event_id,
                status: 'success',
                result: {
                  numero_factura: facturaLocal.folio,
                  pdf_url: facturaLocal.onedrive_pdf_url
                },
                endTime: facturaLocal.fecha
              };
            }
          }
        });
        console.log('[fetchNacionalOrders.fulfilled] Final syncEventStatus:', state.syncEventStatus);
      })
      .addCase(fetchNacionalOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateOrderTicket.pending, (state, action) => {
        const orderId = action.meta.arg.orderId;
        state.updatingTicket[orderId] = true;
        state.error = null;
      })
      .addCase(updateOrderTicket.fulfilled, (state, action) => {
        const { orderId, ticketDespacho } = action.payload;
        state.updatingTicket[orderId] = false;
        const order = state.orders.find(o => o.id === orderId);
        if (order) {
          order.ticket_despacho = ticketDespacho;
        }
      })
      .addCase(updateOrderTicket.rejected, (state, action) => {
        const orderId = action.meta.arg.orderId;
        state.updatingTicket[orderId] = false;
        state.error = action.payload;
      })
      .addCase(fetchInventarioStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchInventarioStatus.fulfilled, (state, action) => {
        if (action.payload) {
          state.inventarioRebajado = true;
          state.inventarioData = action.payload;
        } else {
          state.inventarioRebajado = false;
          state.inventarioData = null;
        }
      })
      .addCase(fetchInventarioStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(rebajarInventario.pending, (state) => {
        state.rebajandoInventario = true;
        state.error = null;
      })
      .addCase(rebajarInventario.fulfilled, (state, action) => {
        state.rebajandoInventario = false;
        state.inventarioRebajado = true;
        state.inventarioData = {
          fecha_ejecucion: action.payload.fecha_ejecucion,
          total_pedidos: action.payload.total_pedidos,
          total_cajas: action.payload.total_cajas
        };
      })
      .addCase(rebajarInventario.rejected, (state, action) => {
        state.rebajandoInventario = false;
        state.error = action.payload;
      })
      // Preview Factura Local Nacional
      .addCase(previewFacturaLocalNacional.pending, (state) => {
        state.previewLoading = true;
        state.previewError = null;
      })
      .addCase(previewFacturaLocalNacional.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.previewData = action.payload;
      })
      .addCase(previewFacturaLocalNacional.rejected, (state, action) => {
        state.previewLoading = false;
        state.previewError = action.payload;
      })
      // Generar Factura Local Nacional
      .addCase(generarFacturaLocalNacional.pending, (state, action) => {
        const pedidoId = action.meta.arg.pedidoId;
        state.generatingFactura[pedidoId] = true;
        state.generationError[pedidoId] = null;
      })
      .addCase(generarFacturaLocalNacional.fulfilled, (state, action) => {
        const { pedidoId, syncEventId } = action.payload;
        state.generatingFactura[pedidoId] = false;
        state.syncEventStatus[pedidoId] = {
          syncEventId,
          status: 'inprogress',
          startTime: new Date().toISOString()
        };
        state.pollingSync[pedidoId] = true;
      })
      .addCase(generarFacturaLocalNacional.rejected, (state, action) => {
        const pedidoId = action.meta.arg.pedidoId;
        state.generatingFactura[pedidoId] = false;
        state.generationError[pedidoId] = action.payload;
      })
      // Fetch Sync Event
      .addCase(fetchSyncEvent.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchSyncEvent.fulfilled, (state, action) => {
        const syncEventId = action.meta.arg.syncEventId;
        // Find pedido with this syncEventId and update its status
        const pedidoId = Object.keys(state.syncEventStatus).find(
          id => state.syncEventStatus[id]?.syncEventId === syncEventId
        );
        if (pedidoId && action.payload) {
          state.syncEventStatus[pedidoId] = {
            ...state.syncEventStatus[pedidoId],
            status: action.payload.status,
            result: action.payload.result,
            error: action.payload.error,
            endTime: action.payload.end_time
          };
          // Stop polling if completed
          if (action.payload.status === 'success' || action.payload.status === 'error') {
            state.pollingSync[pedidoId] = false;
          }
        }
      })
      .addCase(fetchSyncEvent.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearError, resetState } = despachoNacionalesSlice.actions;

// Memoized selectors
const selectOrders = (state) => state.despachoNacionales.orders;
const selectInventarioRebajado = (state) => state.despachoNacionales.inventarioRebajado;

export const selectAllOrdersHaveTickets = createSelector(
  [selectOrders],
  (orders) => {
    if (!orders || orders.length === 0) return false;
    return orders.every(order => order.ticket_despacho && order.ticket_despacho.trim() !== '');
  }
);

export const selectCanRebajarInventario = createSelector(
  [selectAllOrdersHaveTickets, selectInventarioRebajado],
  (allHaveTickets, inventarioRebajado) => {
    return allHaveTickets && !inventarioRebajado;
  }
);

// Selectors for Factura Local Nacional
export const selectPreviewData = (state) => state.despachoNacionales.previewData;
export const selectPreviewLoading = (state) => state.despachoNacionales.previewLoading;
export const selectPreviewError = (state) => state.despachoNacionales.previewError;

export const selectGeneratingFactura = (state) => state.despachoNacionales.generatingFactura;
export const selectGenerationError = (state) => state.despachoNacionales.generationError;
export const selectSyncEventStatus = (state) => state.despachoNacionales.syncEventStatus;
export const selectPollingSync = (state) => state.despachoNacionales.pollingSync;

export const selectSyncEventStatusForPedido = (pedidoId) =>
  createSelector(
    [selectSyncEventStatus],
    (syncEventStatus) => syncEventStatus[pedidoId] || null
  );

export const selectIsGeneratingForPedido = (pedidoId) =>
  createSelector(
    [selectGeneratingFactura],
    (generatingFactura) => generatingFactura[pedidoId] || false
  );

export const selectGenerationErrorForPedido = (pedidoId) =>
  createSelector(
    [selectGenerationError],
    (generationError) => generationError[pedidoId] || null
  );

export default despachoNacionalesSlice.reducer;
